import React from 'react'
import { motion } from 'framer-motion'
import { 
  PhoneIcon, 
  ChatBubbleLeftEllipsisIcon, 
  MapPinIcon,
  CalendarIcon,
  CheckBadgeIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import BaseCard from './BaseCard'
import { BusinessCardProps } from './types'
import { OptimizedImage, formatDistance } from './utils'

const BusinessCard: React.FC<BusinessCardProps> = ({
  business,
  onContact,
  onBookAppointment,
  onViewProfile,
  showDistance = true,
  showServices = true,
  compact = false,
  loading = false,
  onBlock,
  onReinstate,
  onSendMessage,
  adminMode = false,
  showUnpublish = false,
  onUnpublish
  }) => {
    // Handle action clicks
    const handleContact = (e: React.MouseEvent) => {
      e.stopPropagation()
      onContact?.(business.id)
    }
  
    const handleBlock = (e: React.MouseEvent) => {
      e.stopPropagation();
      onBlock?.(business.id);
    };
  

  const handleBookAppointment = (e: React.MouseEvent) => {
    e.stopPropagation()
    onBookAppointment?.(business.id)
  }

  const handleViewProfile = () => {
    onViewProfile?.(business.id)
  }

  // Generate realistic business logo images
  const getBusinessLogo = () => {
    const businessName = business.name.toLowerCase()
    
    // Use professional automotive/tyre business logos and generic business images
    const businessLogos = [
      'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=100&fit=crop&auto=format', // Automotive shop
      'https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=100&h=100&fit=crop&auto=format', // Professional service
      'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&auto=format', // Garage/workshop
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop&auto=format', // Car service
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=100&h=100&fit=crop&auto=format', // Modern business
      'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=100&h=100&fit=crop&auto=format', // Office/professional
      'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=100&h=100&fit=crop&auto=format', // Business professional
      'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=100&h=100&fit=crop&auto=format'  // Clean modern business
    ]
    
    // Select logo based on business name for consistency
    const logoIndex = businessName.charCodeAt(0) % businessLogos.length
    return businessLogos[logoIndex]
  }

  const businessLogo = business.logo_url && business.logo_url.trim().length > 0
    ? business.logo_url
    : getBusinessLogo()

  // Calculate open status based on business.business_hours and current time
  const getIsOpenNow = () => {
    if (!business.business_hours) return false;
    const now = new Date();
    const day = now.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
    const hours = business.business_hours[day];
    if (!hours) return false;

    // Handle different formats
    if (typeof hours === 'string') {
      // Handle string format: "08:00-17:00" or "8:00 AM - 5:30 PM"
      if (hours === 'Closed') return false;

      let openTime: string = '';
      let closeTime: string = '';

      if (hours.includes(' - ')) {
        const timeRange = hours.split(' - ');
        if (timeRange.length !== 2) return false;
        openTime = timeRange[0];
        closeTime = timeRange[1];
      } else if (hours.includes('-')) {
        const timeRange = hours.split('-');
        if (timeRange.length !== 2) return false;
        openTime = timeRange[0];
        closeTime = timeRange[1];
      } else {
        return false;
      }

      const openDate = parseTimeString(openTime.trim(), now);
      if (!openDate) return false;
      const closeDate = parseTimeString(closeTime.trim(), now);
      if (!closeDate) return false;
      return now >= openDate && now <= closeDate;
    } else if (typeof hours === 'object' && hours.open && hours.close) {
      // Handle object format: {open: "08:00", close: "17:00"}
      const [openHour, openMinute] = hours.open.split(':').map(Number);
      const [closeHour, closeMinute] = hours.close.split(':').map(Number);
      const open = new Date(now);
      open.setHours(openHour, openMinute, 0, 0);
      const close = new Date(now);
      close.setHours(closeHour, closeMinute, 0, 0);
      return now >= open && now <= close;
    }

    return false;
  };

  const parseTimeString = (timeStr: string, baseDate: Date): Date | null => {
    // Try 12-hour format first: "8:00 AM" or "8:00 PM"
    const ampmMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampmMatch) {
      let [, hourStr, minuteStr, period] = ampmMatch;
      let hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      if (period.toUpperCase() === 'PM' && hour !== 12) hour += 12;
      if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;

      const date = new Date(baseDate);
      date.setHours(hour, minute, 0, 0);
      return date;
    }

    // Try 24-hour format: "08:00" or "17:00"
    const hour24Match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (hour24Match) {
      const [, hourStr, minuteStr] = hour24Match;
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);

      const date = new Date(baseDate);
      date.setHours(hour, minute, 0, 0);
      return date;
    }

    return null;
  };
  const isOpenNow = getIsOpenNow();

  // Render rating stars
  const renderRating = (rating?: number) => {
    if (!rating) return null
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating ? (
            <StarSolidIcon key={star} className="w-4 h-4 text-yellow-400" />
          ) : (
            <StarIcon key={star} className="w-4 h-4 text-gray-300" />
          )
        ))}
        <span className="text-sm text-gray-600 ml-1">
          {rating.toFixed(1)}
        </span>
        {business.review_count && (
          <span className="text-sm text-gray-500">
            ({business.review_count})
          </span>
        )}
      </div>
    )
  }

  return (
    <BaseCard
      onClick={handleViewProfile}
      loading={loading}
      variant="default"
      size={compact ? 'sm' : 'md'}
      className="group"
      ariaLabel={`${business.name} tyre shop`}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start space-x-3">
          {/* Business Logo */}
          <div className="flex-shrink-0">
            <OptimizedImage
              src={businessLogo}
              alt={`${business.name} logo`}
              className={`${compact ? 'w-10 h-10' : 'w-12 h-12'} rounded-lg ring-2 ring-gray-100`}
            />
          </div>

          {/* Business Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className={`font-semibold text-gray-900 truncate ${compact ? 'text-sm' : 'text-base'}`}>
                    {business.name}
                  </h3>
                  {business.verified && (
                    <CheckBadgeIcon className="w-5 h-5 text-primary-600 flex-shrink-0" />
                  )}
                </div>
                
                {/* Rating */}
                {business.rating && !compact && (
                  <div className="mt-1">
                    {renderRating(business.rating)}
                  </div>
                )}

                {/* Location & Distance */}
                {showDistance && business.distance_km !== undefined && (
                  <div className="flex items-center mt-1 text-sm text-gray-700">
                    <MapPinIcon className="w-4 h-4 mr-1" />
                    <span>{formatDistance(business.distance_km)}</span>
                    {isOpenNow && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                        Open now
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {!compact && business.description && (
          <p className="text-sm text-gray-700 line-clamp-2">
            {business.description}
          </p>
        )}

        {/* Services */}
        {showServices && business.services.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {/* Admin Controls */}
            {adminMode && (
              <div className="flex flex-wrap gap-1 mb-1">
                <button
                  className="px-2 py-1 bg-red-600 text-white rounded text-xs"
                  onClick={handleBlock}
                  type="button"
                >
                  Block
                </button>
                <button
                  className="px-2 py-1 bg-green-600 text-white rounded text-xs"
                  onClick={onReinstate ? (e => { e.stopPropagation(); onReinstate(business.id); }) : undefined}
                  type="button"
                >
                  Reinstate
                </button>
                <button
                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs"
                  onClick={onSendMessage ? (e => { e.stopPropagation(); onSendMessage(business.id); }) : undefined}
                  type="button"
                >
                  Message
                </button>
                <button
                  className="px-2 py-1 bg-gray-700 text-white rounded text-xs"
                  onClick={onViewProfile ? (e => { e.stopPropagation(); onViewProfile(business.id); }) : undefined}
                  type="button"
                >
                  View Shop
                </button>
                {showUnpublish && onUnpublish && (
                  <button
                    className={`px-2 py-1 ${business.published === false ? 'bg-yellow-500' : 'bg-gray-300'} text-black rounded text-xs`}
                    onClick={e => {
                      e.stopPropagation();
                      onUnpublish(business.id, !!business.published);
                    }}
                    type="button"
                  >
                    {business.published === false ? 'Republish' : 'Unpublish'}
                  </button>
                )}
              </div>
            )}
            {business.services.slice(0, compact ? 2 : 4).map((service, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700"
              >
                {service}
              </span>
            ))}
            {business.services.length > (compact ? 2 : 4) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                +{business.services.length - (compact ? 2 : 4)} more
              </span>
            )}
          </div>
        )}

        {/* Contact Information */}
        {!compact && (
          <div className="space-y-2 text-sm text-gray-700">
            {business.contact_phone && (
              <div className="flex items-center">
                <PhoneIcon className="w-4 h-4 mr-2" />
                <span>{business.contact_phone}</span>
              </div>
            )}
            {business.contact_email && (
              <div className="flex items-center">
                <ChatBubbleLeftEllipsisIcon className="w-4 h-4 mr-2" />
                <span className="truncate">{business.contact_email}</span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className={`flex ${compact ? 'space-x-1' : 'space-x-2'} pt-2`}>
          {business.contact_phone && (
            <motion.button
              onClick={handleContact}
              className={`flex-1 flex items-center justify-center space-x-1 ${compact ? 'py-1 px-2' : 'py-2 px-3'} bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg ${compact ? 'text-xs' : 'text-sm'} font-medium transition-colors duration-200`}
              whileTap={{ scale: 0.95 }}
            >
              <PhoneIcon className="w-4 h-4" />
              <span>Call</span>
            </motion.button>
          )}

          <motion.button
            onClick={handleBookAppointment}
            className={`flex-1 flex items-center justify-center space-x-1 ${compact ? 'py-1 px-2' : 'py-2 px-3'} bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white rounded-lg ${compact ? 'text-xs' : 'text-sm'} font-medium transition-all duration-200`}
            whileTap={{ scale: 0.95 }}
          >
            <CalendarIcon className="w-4 h-4" />
            <span>Book</span>
          </motion.button>
        </div>

        {/* Service Radius Indicator */}
        {!compact && business.service_radius > 0 && (
          <div className="text-xs text-gray-500 text-center border-t pt-2">
            Services within {business.service_radius}km radius
          </div>
        )}
      </div>
    </BaseCard>
  )
}

export default BusinessCard